import { useTranslation } from "react-i18next";
import LeafTag from "../../../../common/leaf/LeafTag/LeafTag";
import defaultContent from "../../../../content/downlineProfile";
import commonContent from "../../../../content/common";
import { AmwayFilledLogoIcon } from "../../../../common/icons/AmwayFilledLogoIcon/AmwayFilledLogoIcon";
import ContactRow from "../../shared/contact/ContactRow";
import { getCountryCodeFromAff, getCountryFlagByAff } from "../../../../common/util/getCountryFlag";
import { getCountryNameFromCode } from "../../../../common/util/formatCountryName";
import { storedLocale } from "../../../Authentication/util";
import { RootState, useAppSelector } from "../../../../store";
import { useEffect, useRef, useState } from "react";
import { getDisplayName, resolveDisplayName } from "../../../../common/util/displayName";
import { SponsorType } from "../../shared/types";
import { Address } from "../../../../services/Profile/profileAPI.types";
import { getFormattedAddress } from "../../../Lit/acc-mixins/src/modules/formatters";
import { getCityStateAddress } from "../../../../common/util/address";
import maskAboNumber from "../../../../common/util/maskAboNumber";

export interface KeyInformationSponsorRowProps {
  type: SponsorType;
  affNo: string;
  aboNo: string;
  isValid: boolean;
  displayName?: string;
  phone?: { displayNumber: string; extension: string; number: string; outOfCountryCallingNumber: string };
  email?: string;
  privacyFlag?: boolean;
  name?: string;
  localName?: string;
  address?: Address;
  imageRefUrl?: string;
  isKoreaMarket?: boolean;
}

export const sponsorTagMap: Record<
  SponsorType,
  { key: string; text: string; variant: "blue" | "lightBlue" | "gray" | "lightRed" }
> = {
  sponsor: {
    key: "keyInformation.sponsorTags.sponsor",
    text: defaultContent.keyInformation.sponsorTags.sponsor,
    variant: "blue",
  },
  sponsorPlatinum: {
    key: "keyInformation.sponsorTags.sponsorPlatinum",
    text: defaultContent.keyInformation.sponsorTags.sponsorPlatinum,
    variant: "gray",
  },
  platinum: {
    key: "keyInformation.sponsorTags.platinum",
    text: defaultContent.keyInformation.sponsorTags.platinum,
    variant: "gray",
  },
  fosterSponsor: {
    key: "keyInformation.sponsorTags.fosterSponsor",
    text: defaultContent.keyInformation.sponsorTags.fosterSponsor,
    variant: "lightBlue",
  },
  fosterSponsorPlatinum: {
    key: "keyInformation.sponsorTags.fosterSponsorPlatinum",
    text: defaultContent.keyInformation.sponsorTags.fosterSponsorPlatinum,
    variant: "gray",
  },
  internationalSponsor: {
    key: "keyInformation.sponsorTags.internationalSponsor",
    text: defaultContent.keyInformation.sponsorTags.internationalSponsor,
    variant: "lightRed",
  },
};
const KeyInformationSponsorRow = ({
  type,
  affNo,
  aboNo,
  isValid,
  displayName,
  phone,
  email,
  privacyFlag = false,
  name,
  localName,
  address,
  imageRefUrl = "",
  isKoreaMarket = false,
}: KeyInformationSponsorRowProps) => {
  const { t } = useTranslation(["downlineProfile", "common"]);
  const { showCallIcon, showMessageIcon, showEmailIcon, hideDownlineContactDetails } =
    useAppSelector((state: RootState) => state.boot.configuration.profile) ?? {};
  const hideWhatsApp = useAppSelector((state: RootState) => state.boot.configuration.hideWhatsApp);
  const effectiveHideDownlineContactDetails = hideDownlineContactDetails;
  const sponsorTagInfo = sponsorTagMap[type];
  const confidentialText = t("confidentialUser", commonContent["confidentialUser"], { ns: "common" }) as string;
  const notAvailableText = t("notAvailable", commonContent["notAvailable"], { ns: "common" }) as string;
  const containerRef = useRef<HTMLDivElement>(null);
  const flagRef = useRef<HTMLSpanElement>(null);
  const aboRef = useRef<HTMLParagraphElement>(null);
  const [isWrapped, setIsWrapped] = useState(false);
  const wrappedAtWidth = useRef<number | null>(null);

  const { address: addressFormat = "", addressLinesDivider } =
    useAppSelector((state) => state?.boot?.configuration?.profile?.format) ?? {};
  const defaultAddresses = {
    addressLines: [],
    municipality: "",
    region: "",
    postalCode: "",
    country: "",
    type: "",
  };

  const {
    addressLines: address_lines,
    municipality,
    region,
    postalCode: postal_code,
    country,
  } = address || defaultAddresses;

  const preFormattedAddress = {
    address_lines,
    municipality,
    region,
    postal_code,
    country,
  };

  const addressFormatted = addressFormat
    .split(" ")
    .map((format: string) => format.replace(/[A-Z]/g, (address) => `_${address.toLocaleLowerCase()}`))
    .join(" ");

  const formattedAddress = getFormattedAddress(preFormattedAddress, {
    string: addressFormatted,
    divider: addressLinesDivider,
  });

  const showValidAddress = formattedAddress && formattedAddress !== "--" && privacyFlag !== true;
  const displayAddress = showValidAddress ? formattedAddress.replace(/ \| /g, ", ") : "";
  const effectiveAddress =
    isKoreaMarket && effectiveHideDownlineContactDetails ? getCityStateAddress(address) : displayAddress;
  const isMaskAboNumber =
    (useAppSelector((s) => s.boot.configuration.profile.maskConfidentialAboNumber) ?? false) && privacyFlag;
  useEffect(() => {
    const check = () => {
      if (flagRef.current && aboRef.current && containerRef.current) {
        const wrapped = flagRef.current.getBoundingClientRect().top > aboRef.current.getBoundingClientRect().top;
        const containerWidth = containerRef.current.getBoundingClientRect().width;

        setIsWrapped((prev) => {
          if (wrapped && !prev) {
            wrappedAtWidth.current = containerWidth;
            return true;
          }
          if (!wrapped && prev) {
            wrappedAtWidth.current = null;
            return false;
          }
          return prev;
        });
      }
    };

    const observer = new ResizeObserver(check);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!isValid) return null;
  return (
    <>
      <div className="sponsor-row">
        <div className="sponsor-row--primary-info">
          <div className="profile-icon">
            {imageRefUrl !== "" ? <img src={imageRefUrl} alt="" /> : <AmwayFilledLogoIcon />}
          </div>
          <div className="sponsor-row--primary-info--content">
            <div className="sponsor-row__info">
              <div className="sponsor-row__info__abo">
                <p>
                  {displayName
                    ? getDisplayName(displayName, t)
                    : resolveDisplayName({
                        name,
                        localName,
                        privacyFlag,
                        languageCode: "",
                        locale: storedLocale(),
                        confidentialText,
                        notAvailableText,
                      })}
                </p>
                <div ref={containerRef} className="sponsor-row__info__tags">
                  <LeafTag variant={sponsorTagInfo?.variant}>
                    <span>{`${t(sponsorTagInfo?.key ?? "", sponsorTagInfo?.text ?? "")}`}</span>
                  </LeafTag>
                  <p ref={aboRef}>
                    {isMaskAboNumber ? maskAboNumber(aboNo) : aboNo}
                    {type === "internationalSponsor" && (
                      <span style={{ color: isWrapped ? "transparent" : "inherit" }}> | </span>
                    )}
                  </p>
                  {type === "internationalSponsor" && (
                    <>
                      <span ref={flagRef} className="country-flag">
                        {getCountryFlagByAff(affNo)}
                        {getCountryNameFromCode(getCountryCodeFromAff(affNo), storedLocale())}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            {privacyFlag !== true && effectiveAddress && (
              <p className="sponsor-row--primary-info--content__address">{effectiveAddress}</p>
            )}
          </div>
        </div>
        <div className="sponsor-row--contact">
          <ContactRow
            tealium={{ type: "sponsor", sponsorType: type }}
            iconFlags={{
              showCallIcon,
              showMessageIcon,
              showEmailIcon,
              hideWhatsApp,
              hideDownlineContactDetails: effectiveHideDownlineContactDetails,
            }}
            phone={phone?.displayNumber}
            email={email}
            isKoreaMarket={isKoreaMarket}
          />
        </div>
      </div>
    </>
  );
};

export default KeyInformationSponsorRow;
